import type { IntlShape } from "react-intl";

import type { MLTripSummary } from "@/features/towers/types";
import {
  type DeliveryBenchmarkLane,
  isArrivedBenchmarkEligibleStop,
} from "@/features/towers/utils/deliveryBenchmark";
import {
  getLastDeliveryTimestamp,
  getLastNonTerminalStopBySequence,
  getStopClusterDisplayName,
} from "@/features/towers/utils/tripLatestArrival";
import {
  getNextDeliveryStopSummary,
  tripHasPredictedNextDelivery,
} from "@/features/towers/utils/tripNextDeliveryStop";
import {
  getVisitStatusForStopBenchmark,
  type VisitStatus,
} from "@/features/towers/utils/visitStatus";

import {
  getCurrentEtaTime,
  getTripSummaryClusterDisplayName,
} from "./TripCardsList/tripSummaryDisplay";
import type { TripTab } from "./tripCardsPanelTypes";

export const TRIP_LIST_SORT_OPTIONS_IN_TRANSIT = [
  "upcoming_alerts",
  "next_delivery_soonest",
  "next_delivery_latest",
  "status_most_behind",
  "status_most_ahead",
  "arrival_latest",
  "arrival_earliest",
  "departure_latest",
  "departure_earliest",
  "next_delivery_az",
  "next_delivery_za",
  "destination_az",
  "destination_za",
  "trip_id_az",
  "trip_id_za",
] as const;

export const TRIP_LIST_SORT_OPTIONS_COMPLETED = [
  "schedule_deviation_most_behind",
  "schedule_deviation_most_ahead",
  "arrival_latest",
  "arrival_earliest",
  "departure_latest",
  "departure_earliest",
  "destination_az",
  "destination_za",
  "trip_id_az",
  "trip_id_za",
] as const;

export type TripListSortOptionInTransit =
  (typeof TRIP_LIST_SORT_OPTIONS_IN_TRANSIT)[number];

export type TripListSortOptionCompleted =
  (typeof TRIP_LIST_SORT_OPTIONS_COMPLETED)[number];

export type TripListSortOption =
  | TripListSortOptionInTransit
  | TripListSortOptionCompleted;

export function getTripListSortOptionsForTab(
  tab: TripTab,
): readonly TripListSortOption[] {
  return tab === "IN_TRANSIT"
    ? TRIP_LIST_SORT_OPTIONS_IN_TRANSIT
    : TRIP_LIST_SORT_OPTIONS_COMPLETED;
}

export function isTripListSortOptionValidForTab(
  tab: TripTab,
  option: TripListSortOption,
): boolean {
  return getTripListSortOptionsForTab(tab).includes(option);
}

export function getDefaultTripListSortOption(tab: TripTab): TripListSortOption {
  const options = getTripListSortOptionsForTab(tab);
  if (tab === "IN_TRANSIT" && options.includes("upcoming_alerts")) {
    return "upcoming_alerts";
  }
  if (options.includes("schedule_deviation_most_behind")) {
    return "schedule_deviation_most_behind";
  }
  return options[0]!;
}

export function sanitizeTripListSortForTab(
  option: TripListSortOption,
  tab: TripTab,
): TripListSortOption {
  const options = getTripListSortOptionsForTab(tab);
  return options.includes(option) ? option : getDefaultTripListSortOption(tab);
}

export type TripListSortByTab = Record<TripTab, TripListSortOption>;

export function createDefaultTripListSortByTab(): TripListSortByTab {
  return {
    IN_TRANSIT: getDefaultTripListSortOption("IN_TRANSIT"),
    COMPLETED: getDefaultTripListSortOption("COMPLETED"),
  };
}

/** Maps status/completed sort when switching trip list tabs. */
export function coerceTripListSortOptionForTab(
  option: TripListSortOption,
  tab: TripTab,
): TripListSortOption {
  if (tab === "IN_TRANSIT") {
    if (option === "schedule_deviation_most_behind") {
      return "status_most_behind";
    }
    if (option === "schedule_deviation_most_ahead") {
      return "status_most_ahead";
    }
  } else {
    if (option === "status_most_behind") {
      return "schedule_deviation_most_behind";
    }
    if (option === "status_most_ahead") {
      return "schedule_deviation_most_ahead";
    }
  }
  return option;
}

export function getTripListSortOptionLabel(
  intl: IntlShape,
  option: TripListSortOption,
): string {
  switch (option) {
    case "status_most_behind":
      return intl.formatMessage({
        defaultMessage: "Status: Most delayed",
        description:
          "Trip list sort option — all status dots (completed and upcoming) most delayed",
      });
    case "status_most_ahead":
      return intl.formatMessage({
        defaultMessage: "Status: Most ahead",
        description:
          "Trip list sort option — all status dots (completed and upcoming) most ahead",
      });
    case "schedule_deviation_most_behind":
      return intl.formatMessage({
        defaultMessage: "Completed: Most delayed",
        description:
          "Trip list sort option — completed stops most delayed vs schedule",
      });
    case "schedule_deviation_most_ahead":
      return intl.formatMessage({
        defaultMessage: "Completed: Most ahead",
        description:
          "Trip list sort option — completed stops most ahead vs schedule",
      });
    case "next_delivery_soonest":
      return intl.formatMessage({
        defaultMessage: "Upcoming: Soonest first",
        description: "Trip list sort option — soonest upcoming delivery ETA",
      });
    case "next_delivery_latest":
      return intl.formatMessage({
        defaultMessage: "Upcoming: Latest first",
        description: "Trip list sort option — latest upcoming delivery ETA",
      });
    case "upcoming_alerts":
      return intl.formatMessage({
        defaultMessage: "Upcoming: Alerts",
        description:
          "Trip list sort option — delays then early stops, tiebreak by soonest upcoming ETA",
      });
    case "arrival_latest":
      return intl.formatMessage({
        defaultMessage: "Arrival: Latest first",
        description: "Trip list sort option — latest last arrival",
      });
    case "arrival_earliest":
      return intl.formatMessage({
        defaultMessage: "Arrival: Earliest first",
        description: "Trip list sort option — earliest last arrival",
      });
    case "departure_latest":
      return intl.formatMessage({
        defaultMessage: "Departure: Latest first",
        description: "Trip list sort option — latest trip departure",
      });
    case "departure_earliest":
      return intl.formatMessage({
        defaultMessage: "Departure: Earliest first",
        description: "Trip list sort option — earliest trip departure",
      });
    case "destination_az":
      return intl.formatMessage({
        defaultMessage: "Last Arrival A–Z",
        description:
          "Trip list sort option — last arrival place name ascending",
      });
    case "destination_za":
      return intl.formatMessage({
        defaultMessage: "Last Arrival Z–A",
        description:
          "Trip list sort option — last arrival place name descending",
      });
    case "next_delivery_az":
      return intl.formatMessage({
        defaultMessage: "Upcoming Delivery A–Z",
        description:
          "Trip list sort option — upcoming delivery place name ascending",
      });
    case "next_delivery_za":
      return intl.formatMessage({
        defaultMessage: "Upcoming Delivery Z–A",
        description:
          "Trip list sort option — upcoming delivery place name descending",
      });
    case "trip_id_az":
      return intl.formatMessage({
        defaultMessage: "Trip A–Z",
        description: "Trip list sort option — trip ID ascending",
      });
    case "trip_id_za":
      return intl.formatMessage({
        defaultMessage: "Trip Z–A",
        description: "Trip list sort option — trip ID descending",
      });
    default:
      return option;
  }
}

type SortContext = {
  lane: DeliveryBenchmarkLane;
  clusterDisplayNameById: ReadonlyMap<string, string>;
  listStage: TripTab;
};

function parseTimeMs(value: string | null | undefined): number | null {
  if (value == null || value === "") {
    return null;
  }
  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? ms : null;
}

function compareNullableNumbers(
  a: number | null,
  b: number | null,
  direction: "asc" | "desc",
): number {
  if (a == null && b == null) {
    return 0;
  }
  if (a == null) {
    return 1;
  }
  if (b == null) {
    return -1;
  }
  return direction === "asc" ? a - b : b - a;
}

type TripStatusStopCounts = {
  late: number;
  onTime: number;
  early: number;
};

function addVisitStatusToCounts(
  counts: TripStatusStopCounts,
  status: VisitStatus | null,
): void {
  if (status === "LATE") {
    counts.late++;
  } else if (status === "ON_TIME") {
    counts.onTime++;
  } else if (status === "EARLY") {
    counts.early++;
  }
}

/**
 * Status dots for sorting.
 * @param includeUpcoming When true, counts unarrived predicted next (in-transit status sort).
 */
function getTripStatusStopCounts(
  trip: MLTripSummary,
  lane: DeliveryBenchmarkLane,
  includeUpcoming: boolean,
): TripStatusStopCounts {
  const counts: TripStatusStopCounts = { late: 0, onTime: 0, early: 0 };

  for (const stop of trip.stopSummaries) {
    if (!isArrivedBenchmarkEligibleStop(stop)) {
      continue;
    }
    addVisitStatusToCounts(counts, getVisitStatusForStopBenchmark(stop, lane));
  }

  if (includeUpcoming && tripHasPredictedNextDelivery(trip)) {
    const nextStop = getNextDeliveryStopSummary(trip);
    if (
      nextStop != null &&
      (nextStop.arrivalTime == null || nextStop.arrivalTime === "")
    ) {
      addVisitStatusToCounts(
        counts,
        getVisitStatusForStopBenchmark(nextStop, lane),
      );
    }
  }

  return counts;
}

function getMostBehindSortBucket(counts: TripStatusStopCounts): number {
  if (counts.late > 0) {
    return 0;
  }
  if (counts.onTime > 0) {
    return 1;
  }
  if (counts.early > 0) {
    return 2;
  }
  return 3;
}

function getMostAheadSortBucket(counts: TripStatusStopCounts): number {
  if (counts.early > 0) {
    return 0;
  }
  if (counts.late === 0 && counts.onTime > 0) {
    return 1;
  }
  if (counts.early === 0 && counts.late > 0) {
    return 2;
  }
  return 3;
}

/**
 * Most delayed: trips with delays (by most lates) → on-time-only trips (by fewest
 * on-time) → early-only trips (by fewest early).
 */
function compareTripStatusCountsMostBehind(
  a: TripStatusStopCounts,
  b: TripStatusStopCounts,
): number {
  const bucketCmp = getMostBehindSortBucket(a) - getMostBehindSortBucket(b);
  if (bucketCmp !== 0) {
    return bucketCmp;
  }

  const bucket = getMostBehindSortBucket(a);
  if (bucket === 0) {
    const lateCmp = b.late - a.late;
    if (lateCmp !== 0) {
      return lateCmp;
    }
    const onTimeCmp = a.onTime - b.onTime;
    if (onTimeCmp !== 0) {
      return onTimeCmp;
    }
    return a.early - b.early;
  }
  if (bucket === 1) {
    const mixedEarlyCmp = (a.early > 0 ? 1 : 0) - (b.early > 0 ? 1 : 0);
    if (mixedEarlyCmp !== 0) {
      return mixedEarlyCmp;
    }
    const onTimeCmp = a.onTime - b.onTime;
    if (onTimeCmp !== 0) {
      return onTimeCmp;
    }
    return a.early - b.early;
  }
  if (bucket === 2) {
    return a.early - b.early;
  }
  return 0;
}

/**
 * Most ahead: trips with early (by most early) → on-time without early/delays
 * (by most on-time) → delays without early (by fewest lates).
 */
function compareTripStatusCountsMostAhead(
  a: TripStatusStopCounts,
  b: TripStatusStopCounts,
): number {
  const bucketCmp = getMostAheadSortBucket(a) - getMostAheadSortBucket(b);
  if (bucketCmp !== 0) {
    return bucketCmp;
  }

  const bucket = getMostAheadSortBucket(a);
  if (bucket === 0) {
    const earlyCmp = b.early - a.early;
    if (earlyCmp !== 0) {
      return earlyCmp;
    }
    const onTimeCmp = b.onTime - a.onTime;
    if (onTimeCmp !== 0) {
      return onTimeCmp;
    }
    return a.late - b.late;
  }
  if (bucket === 1) {
    return b.onTime - a.onTime;
  }
  if (bucket === 2) {
    return a.late - b.late;
  }
  return 0;
}

function compareUpcomingDeliveryEta(
  a: MLTripSummary,
  b: MLTripSummary,
): number {
  return compareNullableNumbers(
    parseTimeMs(getCurrentEtaTime(a)),
    parseTimeMs(getCurrentEtaTime(b)),
    "asc",
  );
}

function compareUpcomingDeliverySortTieBreak(
  a: MLTripSummary,
  b: MLTripSummary,
): number {
  return (
    compareUpcomingDeliveryEta(a, b) ||
    compareLastArrivalLatest(a, b) ||
    compareDepartureLatest(a, b)
  );
}

/** Delays (most lates, then soonest ETA) → early (most early, then soonest ETA). */
function compareUpcomingAlerts(
  a: MLTripSummary,
  b: MLTripSummary,
  lane: DeliveryBenchmarkLane,
): number {
  const countsA = getTripStatusStopCounts(a, lane, true);
  const countsB = getTripStatusStopCounts(b, lane, true);

  const aHasLate = countsA.late > 0;
  const bHasLate = countsB.late > 0;
  if (aHasLate || bHasLate) {
    if (aHasLate && !bHasLate) {
      return -1;
    }
    if (!aHasLate && bHasLate) {
      return 1;
    }
    const lateCmp = countsB.late - countsA.late;
    if (lateCmp !== 0) {
      return lateCmp;
    }
    return compareUpcomingDeliveryEta(a, b);
  }

  const aHasEarly = countsA.early > 0;
  const bHasEarly = countsB.early > 0;
  if (aHasEarly || bHasEarly) {
    if (aHasEarly && !bHasEarly) {
      return -1;
    }
    if (!aHasEarly && bHasEarly) {
      return 1;
    }
    const earlyCmp = countsB.early - countsA.early;
    if (earlyCmp !== 0) {
      return earlyCmp;
    }
    return compareUpcomingDeliveryEta(a, b);
  }

  return compareUpcomingDeliveryEta(a, b);
}

function getLastDeliverySortLabel(
  trip: MLTripSummary,
  clusterDisplayNameById: ReadonlyMap<string, string>,
): string {
  const lastStop = getLastNonTerminalStopBySequence(trip);
  if (lastStop == null) {
    return "";
  }
  const clusterId = lastStop.clusterId ?? "";
  return (
    clusterDisplayNameById.get(clusterId) ??
    getStopClusterDisplayName(lastStop.clusterName, clusterId) ??
    clusterId
  );
}

function getNextDeliverySortLabel(
  trip: MLTripSummary,
  clusterDisplayNameById: ReadonlyMap<string, string>,
): string {
  const clusterId = trip.predictedNextStopClusterId ?? "";
  return (
    clusterDisplayNameById.get(clusterId) ??
    getTripSummaryClusterDisplayName(
      trip.predictedNextStopClusterName,
      trip.predictedNextStopClusterId,
    ) ??
    clusterId
  );
}

function isLocaleSortKeyBlank(value: string): boolean {
  return value.trim() === "";
}

function compareLocaleStrings(
  a: string,
  b: string,
  direction: "asc" | "desc",
): number {
  const aBlank = isLocaleSortKeyBlank(a);
  const bBlank = isLocaleSortKeyBlank(b);
  if (aBlank && bBlank) {
    return 0;
  }
  if (aBlank) {
    return 1;
  }
  if (bBlank) {
    return -1;
  }
  const cmp = a.localeCompare(b, undefined, { sensitivity: "base" });
  return direction === "asc" ? cmp : -cmp;
}

function compareLastArrivalLatest(a: MLTripSummary, b: MLTripSummary): number {
  return compareNullableNumbers(
    parseTimeMs(getLastDeliveryTimestamp(a)),
    parseTimeMs(getLastDeliveryTimestamp(b)),
    "desc",
  );
}

function compareDepartureLatest(a: MLTripSummary, b: MLTripSummary): number {
  return compareNullableNumbers(
    parseTimeMs(a.startTime),
    parseTimeMs(b.startTime),
    "desc",
  );
}

function compareUpcomingStatusCountsMostBehind(
  a: MLTripSummary,
  b: MLTripSummary,
  lane: DeliveryBenchmarkLane,
): number {
  return compareTripStatusCountsMostBehind(
    getTripStatusStopCounts(a, lane, true),
    getTripStatusStopCounts(b, lane, true),
  );
}

/** After primary in-transit sort ties (status, alerts, delivery stop count, etc.). */
function compareInTransitSharedTieBreak(
  a: MLTripSummary,
  b: MLTripSummary,
  lane: DeliveryBenchmarkLane,
): number {
  return (
    compareUpcomingAlerts(a, b, lane) ||
    compareUpcomingStatusCountsMostBehind(a, b, lane) ||
    compareLastArrivalLatest(a, b) ||
    compareDepartureLatest(a, b)
  );
}

function compareAfterUpcomingAlertsTie(
  a: MLTripSummary,
  b: MLTripSummary,
  lane: DeliveryBenchmarkLane,
): number {
  return (
    compareUpcomingStatusCountsMostBehind(a, b, lane) ||
    compareLastArrivalLatest(a, b) ||
    compareDepartureLatest(a, b)
  );
}

function compareTrips(
  a: MLTripSummary,
  b: MLTripSummary,
  option: TripListSortOption,
  ctx: SortContext,
): number {
  const tieBreak = a.tripId.localeCompare(b.tripId);

  switch (option) {
    case "status_most_behind": {
      const cmp = compareTripStatusCountsMostBehind(
        getTripStatusStopCounts(a, ctx.lane, true),
        getTripStatusStopCounts(b, ctx.lane, true),
      );
      return cmp || compareInTransitSharedTieBreak(a, b, ctx.lane) || tieBreak;
    }
    case "status_most_ahead": {
      const cmp = compareTripStatusCountsMostAhead(
        getTripStatusStopCounts(a, ctx.lane, true),
        getTripStatusStopCounts(b, ctx.lane, true),
      );
      return cmp || compareInTransitSharedTieBreak(a, b, ctx.lane) || tieBreak;
    }
    case "schedule_deviation_most_behind": {
      const cmp = compareTripStatusCountsMostBehind(
        getTripStatusStopCounts(a, ctx.lane, false),
        getTripStatusStopCounts(b, ctx.lane, false),
      );
      return cmp || compareLastArrivalLatest(a, b) || tieBreak;
    }
    case "schedule_deviation_most_ahead": {
      const cmp = compareTripStatusCountsMostAhead(
        getTripStatusStopCounts(a, ctx.lane, false),
        getTripStatusStopCounts(b, ctx.lane, false),
      );
      return cmp || compareLastArrivalLatest(a, b) || tieBreak;
    }
    case "next_delivery_soonest":
      return (
        compareNullableNumbers(
          parseTimeMs(getCurrentEtaTime(a)),
          parseTimeMs(getCurrentEtaTime(b)),
          "asc",
        ) ||
        compareLastArrivalLatest(a, b) ||
        compareDepartureLatest(a, b) ||
        tieBreak
      );
    case "next_delivery_latest":
      return (
        compareNullableNumbers(
          parseTimeMs(getCurrentEtaTime(a)),
          parseTimeMs(getCurrentEtaTime(b)),
          "desc",
        ) ||
        compareLastArrivalLatest(a, b) ||
        compareDepartureLatest(a, b) ||
        tieBreak
      );
    case "upcoming_alerts":
      return (
        compareUpcomingAlerts(a, b, ctx.lane) ||
        compareAfterUpcomingAlertsTie(a, b, ctx.lane) ||
        tieBreak
      );
    case "arrival_latest":
      return (
        compareNullableNumbers(
          parseTimeMs(getLastDeliveryTimestamp(a)),
          parseTimeMs(getLastDeliveryTimestamp(b)),
          "desc",
        ) || tieBreak
      );
    case "arrival_earliest":
      return (
        compareNullableNumbers(
          parseTimeMs(getLastDeliveryTimestamp(a)),
          parseTimeMs(getLastDeliveryTimestamp(b)),
          "asc",
        ) || tieBreak
      );
    case "departure_latest":
      return (
        compareNullableNumbers(
          parseTimeMs(a.startTime),
          parseTimeMs(b.startTime),
          "desc",
        ) || tieBreak
      );
    case "departure_earliest":
      return (
        compareNullableNumbers(
          parseTimeMs(a.startTime),
          parseTimeMs(b.startTime),
          "asc",
        ) || tieBreak
      );
    case "destination_az": {
      const cmp = compareLocaleStrings(
        getLastDeliverySortLabel(a, ctx.clusterDisplayNameById),
        getLastDeliverySortLabel(b, ctx.clusterDisplayNameById),
        "asc",
      );
      return (
        cmp ||
        compareLastArrivalLatest(a, b) ||
        compareDepartureLatest(a, b) ||
        tieBreak
      );
    }
    case "destination_za": {
      const cmp = compareLocaleStrings(
        getLastDeliverySortLabel(a, ctx.clusterDisplayNameById),
        getLastDeliverySortLabel(b, ctx.clusterDisplayNameById),
        "desc",
      );
      return (
        cmp ||
        compareLastArrivalLatest(a, b) ||
        compareDepartureLatest(a, b) ||
        tieBreak
      );
    }
    case "next_delivery_az": {
      const cmp = compareLocaleStrings(
        getNextDeliverySortLabel(a, ctx.clusterDisplayNameById),
        getNextDeliverySortLabel(b, ctx.clusterDisplayNameById),
        "asc",
      );
      return cmp || compareUpcomingDeliverySortTieBreak(a, b) || tieBreak;
    }
    case "next_delivery_za": {
      const cmp = compareLocaleStrings(
        getNextDeliverySortLabel(a, ctx.clusterDisplayNameById),
        getNextDeliverySortLabel(b, ctx.clusterDisplayNameById),
        "desc",
      );
      return cmp || compareUpcomingDeliverySortTieBreak(a, b) || tieBreak;
    }
    case "trip_id_az":
      return (
        compareLocaleStrings(a.tripId, b.tripId, "asc") ||
        (ctx.listStage === "IN_TRANSIT"
          ? compareUpcomingDeliverySortTieBreak(a, b)
          : compareLastArrivalLatest(a, b)) ||
        tieBreak
      );
    case "trip_id_za":
      return (
        compareLocaleStrings(a.tripId, b.tripId, "desc") ||
        (ctx.listStage === "IN_TRANSIT"
          ? compareUpcomingDeliverySortTieBreak(a, b)
          : compareLastArrivalLatest(a, b)) ||
        tieBreak
      );
    default:
      return tieBreak;
  }
}

export function sortTripSummariesForList(
  trips: readonly MLTripSummary[],
  sortBy: TripListSortOption,
  lane: DeliveryBenchmarkLane,
  clusterDisplayNameById: ReadonlyMap<string, string>,
  listStage: TripTab,
): MLTripSummary[] {
  const ctx: SortContext = { lane, clusterDisplayNameById, listStage };
  return [...trips].sort((a, b) => compareTrips(a, b, sortBy, ctx));
}
